import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MDEditor from "@uiw/react-md-editor";
import { api, formatApiError, formatDate } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Save, History, AlertTriangle, Eye, X, HelpCircle, Sparkles,
} from "lucide-react";

export default function AdminPromptEditorPage() {
  const { promptKey } = useParams();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Metadata desde el backend (modelos disponibles + ayuda)
  const [meta, setMeta] = useState({ available_models: [], variable_usage_help: "" });
  const [helpOpen, setHelpOpen] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // Editable fields
  const [content, setContent] = useState("");
  const [description, setDescription] = useState("");
  const [usageContext, setUsageContext] = useState("");
  const [model, setModel] = useState("");
  const [variables, setVariables] = useState([]);
  const [variableInput, setVariableInput] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/prompts/${promptKey}`);
      setPrompt(data);
      setContent(data.content || "");
      setDescription(data.description || "");
      setUsageContext(data.usage_context || "");
      setModel(data.model || "");
      setVariables(data.variables || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando prompt");
    } finally {
      setLoading(false);
    }
  }, [promptKey]);

  useEffect(() => { load(); }, [load]);

  // Carga lazy de metadata (modelos + ayuda)
  useEffect(() => {
    api.get("/admin/prompts/meta")
      .then(({ data }) => setMeta(data))
      .catch(() => {});
  }, []);

  const isDirty = useMemo(() => prompt && (
    content !== prompt.content ||
    description !== (prompt.description || "") ||
    usageContext !== (prompt.usage_context || "") ||
    model !== (prompt.model || "") ||
    JSON.stringify(variables) !== JSON.stringify(prompt.variables || [])
  ), [prompt, content, description, usageContext, model, variables]);

  // Cuál cambio motiva nueva versión
  const modelChanged = prompt && model !== (prompt.model || "");
  const contentChanged = prompt && content !== prompt.content;

  const saveNewVersion = async () => {
    if (!content.trim()) {
      toast.error("El contenido no puede estar vacío");
      return;
    }
    if (!isDirty) {
      toast.info("No hay cambios para guardar");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/prompts/${promptKey}/version`, {
        content,
        description,
        usage_context: usageContext,
        model,
        variables,
        activate: true,
      });
      toast.success(`Versión v${data.version} guardada y activada`);
      setPrompt(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const onCancelClick = () => {
    if (!isDirty) {
      navigate("/admin/prompts");
      return;
    }
    setConfirmDiscardOpen(true);
  };

  const confirmDiscard = () => {
    setContent(prompt.content || "");
    setDescription(prompt.description || "");
    setUsageContext(prompt.usage_context || "");
    setModel(prompt.model || "");
    setVariables(prompt.variables || []);
    setVariableInput("");
    setConfirmDiscardOpen(false);
    toast.info("Cambios descartados");
  };

  const addVariable = () => {
    const v = variableInput.trim();
    if (!v) return;
    if (variables.includes(v)) {
      toast.error("Variable ya existe");
      return;
    }
    setVariables([...variables, v]);
    setVariableInput("");
  };

  const removeVariable = (v) => setVariables(variables.filter((x) => x !== v));

  if (loading || !prompt) {
    return <div className="p-6 text-center text-gray-500">Cargando…</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-color-mode="light">
      <div className="mb-6">
        <Button variant="ghost" size="sm" onClick={onCancelClick} className="mb-2" data-testid="back-to-prompts">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver a prompts
        </Button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-serif text-3xl text-gray-900 mb-1">{prompt.name}</h1>
            <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
              <code className="bg-gray-100 px-1.5 py-0.5">{prompt.prompt_key}</code>
              <span>·</span>
              <span>v{prompt.version} activa</span>
              <span>·</span>
              <span>Actualizado {formatDate(prompt.updated_at)}</span>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => navigate(`/admin/prompts/${promptKey}/history`)}
              data-testid="goto-history"
            >
              <History className="w-4 h-4 mr-2" /> Historial
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowPreview(!showPreview)}
              data-testid="toggle-preview"
            >
              <Eye className="w-4 h-4 mr-2" /> {showPreview ? "Ocultar" : "Ver"} contexto
            </Button>
            {isDirty && (
              <Button
                variant="outline"
                onClick={onCancelClick}
                disabled={saving}
                data-testid="cancel-editing"
                className="border-gray-300"
              >
                <X className="w-4 h-4 mr-2" /> Cancelar
              </Button>
            )}
            <Button
              onClick={saveNewVersion}
              disabled={saving || !isDirty}
              className="bg-brand-burgundy hover:bg-brand-burgundy-light text-white"
              data-testid="save-prompt"
            >
              <Save className="w-4 h-4 mr-2" /> {saving ? "Guardando..." : "Guardar como nueva versión"}
            </Button>
          </div>
        </div>
        {isDirty && (
          <div className="mt-3 p-2 border border-amber-300 bg-amber-50 rounded-sm text-xs text-amber-900 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              Hay cambios sin guardar. Al guardar se crea una nueva versión y se activa automáticamente.
              {modelChanged && !contentChanged && (
                <div className="mt-1 text-amber-800">
                  ⚙️ Solo cambiaste el modelo (<code className="bg-amber-100 px-1">{prompt.model || "—"}</code> → <code className="bg-amber-100 px-1">{model}</code>): se clonará el contenido actual en una versión nueva. Esto mantiene trazabilidad limpia del A/B testing.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Contexto / metadata */}
      {showPreview && (
        <div className="mb-6 border border-brand-burgundy/20 bg-brand-cream/30 rounded-sm p-4">
          <h3 className="text-sm font-semibold text-brand-burgundy mb-2">Contexto de uso</h3>
          <div className="prose prose-sm max-w-none" data-color-mode="light">
            <MDEditor.Markdown source={usageContext} style={{ background: "transparent" }} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Editor principal */}
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-600 mb-1 block">
              Contenido del prompt (markdown soportado)
            </Label>
            <MDEditor
              value={content}
              onChange={(v) => setContent(v || "")}
              height={500}
              preview="edit"
              visibleDragbar={false}
              data-color-mode="light"
            />
            <div className="text-xs text-gray-500 mt-1 flex items-center justify-between">
              <span>{content.length} caracteres · {content.split("\n").length} líneas</span>
              <button
                type="button"
                onClick={() => setHelpOpen(true)}
                className="text-brand-burgundy hover:underline inline-flex items-center gap-1"
                data-testid="variable-help-btn"
              >
                <HelpCircle className="w-3 h-3" /> ¿Cómo usar variables?
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar metadata */}
        <aside className="space-y-4">
          <div>
            <Label htmlFor="prompt-desc" className="text-xs uppercase tracking-wider text-gray-600">
              Descripción
            </Label>
            <Textarea
              id="prompt-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Resumen breve del objetivo del prompt"
              data-testid="prompt-description"
            />
          </div>

          <div>
            <Label htmlFor="prompt-model" className="text-xs uppercase tracking-wider text-gray-600 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Modelo recomendado
            </Label>
            <select
              id="prompt-model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="h-10 w-full border border-gray-300 rounded-sm px-2 text-sm bg-white"
              data-testid="prompt-model"
            >
              <option value="">— Sin modelo definido —</option>
              {meta.available_models?.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
              {/* Si el valor actual no está en la lista (e.g. modelo legacy), mostrarlo igual */}
              {model && !meta.available_models?.some((m) => m.id === model) && (
                <option value={model}>{model} (legacy)</option>
              )}
            </select>
            <p className="text-[10px] text-gray-500 mt-1">
              Cambiar el modelo crea automáticamente una nueva versión del prompt con el contenido clonado para trazabilidad del A/B.
            </p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-600">
              Variables esperadas
            </Label>
            <div className="flex flex-wrap gap-1 mb-2">
              {variables.map((v) => (
                <span key={v} className="inline-flex items-center gap-1 bg-brand-cream text-brand-burgundy text-[11px] px-2 py-1 rounded-sm border border-brand-burgundy/20">
                  <code>{v}</code>
                  <button
                    onClick={() => removeVariable(v)}
                    className="hover:text-red-700"
                    data-testid={`remove-var-${v}`}
                  >×</button>
                </span>
              ))}
              {variables.length === 0 && <span className="text-xs text-gray-400">Ninguna</span>}
            </div>
            <div className="flex gap-1">
              <Input
                value={variableInput}
                onChange={(e) => setVariableInput(e.target.value)}
                placeholder="nombre_entidad"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addVariable(); } }}
                className="text-xs"
                data-testid="variable-input"
              />
              <Button size="sm" variant="outline" onClick={addVariable} data-testid="add-variable">Añadir</Button>
            </div>
            <p className="text-[10px] text-gray-500 mt-1">
              Usa <code>snake_case</code>. Referencia en el contenido como <code>{"{{nombre_entidad}}"}</code>.
            </p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-600">
              Contexto de uso (markdown)
            </Label>
            <Textarea
              value={usageContext}
              onChange={(e) => setUsageContext(e.target.value)}
              rows={10}
              placeholder="## ¿Dónde se usa?&#10;..."
              className="font-mono text-xs"
              data-testid="prompt-usage-context"
            />
            <p className="text-[10px] text-gray-400 mt-1">
              Documenta dónde se usa este prompt y su objetivo. Soporta markdown.
            </p>
          </div>
        </aside>
      </div>

      {/* Modal: Ayuda de variables */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="variable-help-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-brand-burgundy" /> Ayuda — Variables en prompts
            </DialogTitle>
            <DialogDescription>
              Cómo declarar y usar variables sustituibles dentro del contenido de un prompt.
            </DialogDescription>
          </DialogHeader>
          <div className="prose prose-sm max-w-none mt-2" data-color-mode="light">
            <MDEditor.Markdown
              source={meta.variable_usage_help || "Cargando ayuda…"}
              style={{ background: "transparent" }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHelpOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Confirmar descarte */}
      <Dialog open={confirmDiscardOpen} onOpenChange={setConfirmDiscardOpen}>
        <DialogContent className="max-w-md" data-testid="discard-confirm-modal">
          <DialogHeader>
            <DialogTitle>¿Descartar cambios?</DialogTitle>
            <DialogDescription>
              Tienes cambios sin guardar. Si continúas, se perderán.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDiscardOpen(false)} data-testid="discard-cancel">
              Seguir editando
            </Button>
            <Button
              onClick={confirmDiscard}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="discard-confirm"
            >
              Descartar y salir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
